import { GoogleGenerativeAI } from "@google/generative-ai";

class TranslationEnhancementService {
  constructor(apiKey) {
    this.genAI = new GoogleGenerativeAI(apiKey);
    this.cache = new Map();
  }

  getCacheKey(text, direction, enhance) {
    return `${direction}_${enhance ? 'enhanced' : 'basic'}_${text}`;
  }

  async translateAndEnhance(text, direction, options = {}) {
    const cacheKey = this.getCacheKey(text, direction, options.enhance || false);
    
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    const model = this.genAI.getGenerativeModel({ model: "gemini-1.5-flash-002" });

    try {
      const prompt = this.constructPrompt(text, direction, options);
      const result = await model.generateContent(prompt);
      const response = await result.response.text();
      
      const translationResult = await this.parseResponse(response, text, direction);
      this.cache.set(cacheKey, translationResult);
      
      return translationResult;
    } catch (error) {
      console.error("Translation/Enhancement error:", error);
      throw new Error(`Failed to ${direction === 'TR_TO_EN' ? 'translate to English' : 'translate to Turkish'}`);
    }
  }

  constructPrompt(text, direction, options = {}) {
    if (direction === 'TR_TO_EN') {
      if (options.enhance) {
        return `
Task: Translate the following Turkish text to English AND enhance it for architectural visualization:

Turkish Input: "${text}"

Requirements:
1. First provide the direct English translation
2. Then provide an enhanced version with additional architectural details
3. Format the response exactly as:
TRANSLATION: [direct translation]
ENHANCED: [enhanced version with architectural details, focusing on ${options.mode || 'interior'} design and ${options.style || 'modern'} style]

Example Format:
TRANSLATION: Modern living room with large windows
ENHANCED: Spacious contemporary living room, floor-to-ceiling windows, natural light, minimalist furniture, neutral color palette, ambient lighting
`;
      } else {
        return `
Translate this Turkish text to English, maintaining architectural and design context:
"${text}"
Provide ONLY the translation, no explanations.`;
      }
    } else {
      return `
Translate this English architectural description to natural Turkish:
"${text}"
Provide ONLY the translation, no explanations.`;
    }
  }

  async parseResponse(response, originalText, direction) {
    const lines = response.split('\n');
    
    if (lines.some(line => line.startsWith('TRANSLATION:'))) {
      // Enhanced format
      const translation = lines
        .find(line => line.startsWith('TRANSLATION:'))
        ?.replace('TRANSLATION:', '')
        .trim();
      
      const enhanced = lines
        .find(line => line.startsWith('ENHANCED:'))
        ?.replace('ENHANCED:', '')
        .trim();

      // If this was an enhancement (TR->EN->Enhanced->TR), translate the enhanced text back to Turkish
      if (direction === 'TR_TO_EN' && enhanced) {
        const backToTurkish = await this.translateAndEnhance(enhanced, 'EN_TO_TR');
        return {
          translatedText: translation || response.trim(),
          enhancedText: backToTurkish.translatedText,
          originalText,
          direction
        };
      } else {
        return {
          translatedText: translation || response.trim(),
          enhancedText: enhanced,
          originalText,
          direction
        };
      }
    }

    // Basic translation
    return {
      translatedText: response.trim(),
      originalText,
      direction
    };
  }
}

export const translationService = new TranslationEnhancementService(process.env.REACT_APP_GEMINI_API_KEY);
